import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

function FeeStructure() {
  const { classId } = useParams(); // 'new' or existing fee structure ID
  const navigate = useNavigate();

  // ===== Session & Class =====
  const storedSessionId = localStorage.getItem('currentSessionId');
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(storedSessionId || '');
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');

  // ===== Fee Structure Fields =====
  const [studentType, setStudentType] = useState('');
  const [medium, setMedium] = useState('');
  const [feeHeads, setFeeHeads] = useState([]);
  const [feeHeadValues, setFeeHeadValues] = useState({});
  const [monthlyFees, setMonthlyFees] = useState(Array(12).fill(''));
  const [remarks, setRemarks] = useState('');
  const [totalFees, setTotalFees] = useState(0);

  const months = [
    'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December', 'January', 'February', 'March'
  ];

  // ===== Fetch all sessions =====
  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/sessions/allSessions`)
      .then(res => setSessions(res.data))
      .catch(err => console.error(err));
  }, []);

  // ===== Fetch classes when session changes =====
  useEffect(() => {
    if (!selectedSession) return;

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses?sessionId=${selectedSession}`)
      .then(res => setClasses(res.data))
      .catch(err => console.error(err));
  }, [selectedSession]);

  // ===== Fetch fee heads when session changes =====
  useEffect(() => {
    if (!selectedSession) return;

    axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/feeheads/getFeeHeads`, {
      params: { sessionId: selectedSession }
    })
    .then(res => {
      setFeeHeads(res.data);
      // Initialize fee head values if empty
      const initial = res.data.reduce((acc, head) => ({ ...acc, [head.headName]: '' }), {});
      setFeeHeadValues(prev => ({ ...initial, ...prev }));
    })
    .catch(err => console.error(err));
  }, [selectedSession]);

  // ===== Fetch fee structure for editing =====
  useEffect(() => {
    if (classId === 'new') return;

   axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/feeStructures/getFeeStructureById/${classId}`, {
  params: { sessionId: storedSessionId } // Pass sessionId here
})
.then(res => {
  const f = res.data;
  setSelectedSession(f.sessionId || storedSessionId);
  setSelectedClass(f.class);
  setStudentType(f.studentType);
  setMedium(f.medium);
  setRemarks(f.remarks);
  setFeeHeadValues(f.feeHeadValues || {});
  setMonthlyFees([
    f.month1 || 0, f.month2 || 0, f.month3 || 0, f.month4 || 0,
    f.month5 || 0, f.month6 || 0, f.month7 || 0, f.month8 || 0,
    f.month9 || 0, f.month10 || 0, f.month11 || 0, f.month12 || 0
  ]);
  setTotalFees(f.totalfees || 0);
})
.catch(err => console.error(err));
}, [classId, storedSessionId]);

  // ===== Calculate total fees =====
  useEffect(() => {
    const headSum = Object.values(feeHeadValues).reduce((sum, val) => sum + parseFloat(val || 0), 0);
    const monthSum = monthlyFees.reduce((sum, val) => sum + parseFloat(val || 0), 0);
    setTotalFees(headSum + monthSum);
  }, [feeHeadValues, monthlyFees]);

  // ===== Handlers =====
  const handleFeeHeadChange = (head, value) => {
    setFeeHeadValues(prev => ({ ...prev, [head]: value }));
  };

  const handleMonthlyChange = (i, value) => {
    const updated = [...monthlyFees];
    updated[i] = value;
    setMonthlyFees(updated);
  };

  const fillMonthlyFees = () => {
    if (monthlyFees[0]) {
      setMonthlyFees(Array(12).fill(monthlyFees[0]));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSession || !selectedClass || !studentType || !medium) {
      alert("Please fill all required fields.");
      return;
    }

    const numericFeeHeads = Object.fromEntries(
      Object.entries(feeHeadValues).map(([k, v]) => [k, Number(v) || 0])
    );
    const numericMonths = monthlyFees.map(v => Number(v) || 0);

    const data = {
      sessionId: selectedSession,
      class: selectedClass,
      studentType,
      medium,
      remarks,
      feeHeadValues: numericFeeHeads,
      totalfees: totalFees,
      month1: numericMonths[0],
      month2: numericMonths[1],
      month3: numericMonths[2],
      month4: numericMonths[3],
      month5: numericMonths[4],
      month6: numericMonths[5],
      month7: numericMonths[6],
      month8: numericMonths[7],
      month9: numericMonths[8],
      month10: numericMonths[9],
      month11: numericMonths[10],
      month12: numericMonths[11],
    };

    try {
      if (classId === 'new') {
        await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/feeStructures/createFeeStructure`, data, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
        });
      } else {
        await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/feeStructures/updateFeeStructure/${classId}`, data,
          {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
          }
        );
      }
      alert('Fee structure saved successfully');
      navigate('/fee-list');
    } catch (err) {
      console.error(err.response?.data || err);
      alert("Error saving fee structure.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white shadow-md rounded-lg">
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Session & Class */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium text-gray-700 mb-1">Session</label>
            <select
              value={selectedSession}
              onChange={e => setSelectedSession(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="" disabled>Select Session</option>
              {sessions.map(s => (
                <option key={s._id} value={s._id}>
                  {s._id === storedSessionId ? `${s.sessionName} (Current)` : s.sessionName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-1">Class</label>
            <select
              value={selectedClass}
              onChange={e => setSelectedClass(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="" disabled>Select Class</option>
              {classes.map(c => <option key={c._id} value={c.className}>{c.className}</option>)}
            </select>
          </div>
        </div>

        {/* Medium & Student Type */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block font-medium text-gray-700 mb-1">Medium</label>
            <select
              value={medium}
              onChange={e => setMedium(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="" disabled>Select Medium</option>
              <option value="English">English</option>
              <option value="Hindi">Hindi</option>
            </select>
          </div>

          <div>
            <label className="block font-medium text-gray-700 mb-1">Student Type</label>
            <select
              value={studentType}
              onChange={e => setStudentType(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            >
              <option value="" disabled>Select Type</option>
              <option value="New Student">New Student</option>
              <option value="Old Student">Old Student</option>
            </select>
          </div>
        </div>

        {/* Fee Heads */}
        <div>
          <label className="block font-medium text-gray-700 mb-2">Fee Heads</label>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {feeHeads.map(head => (
              <div key={head._id}>
                <label className="block mb-1 text-gray-700">{head.headName}</label>
                <input
                  type="number"
                  value={feeHeadValues[head.headName] || ''}
                  onChange={e => handleFeeHeadChange(head.headName, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Monthly Fees */}
        <div>
          <label className="block font-medium text-gray-700 mb-2">Monthly Fees</label>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {months.map((month, i) => (
              <div key={i}>
                <label className="block mb-1 text-gray-700">{month}</label>
                <input
                  type="number"
                  value={monthlyFees[i]}
                  onChange={e => handleMonthlyChange(i, e.target.value)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={fillMonthlyFees}
            className="mt-2 text-blue-500 underline"
          >
            Fill all months with first month fee
          </button>
        </div>

        {/* Remarks & Total */}
        <div>
          <label className="block font-medium text-gray-700 mb-1">Remarks</label>
          <textarea
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            className="w-full border border-gray-300 rounded-md px-3 py-2"
          />
        </div>

        <div>
          <label className="block font-medium text-gray-700 mb-1">Total Fees</label>
          <input
            type="number"
            value={totalFees}
            readOnly
            className="w-full border border-gray-300 rounded-md px-3 py-2 bg-gray-100"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="w-full sm:w-auto py-2 px-4 bg-blue-600 text-white font-semibold rounded-md hover:bg-blue-700"
        >
          Save Fee Structure
        </button>
      </form>
    </div>
  );
}

export default FeeStructure;
